// ./src/components/features/Dashboard/DashboardPlanUsage.tsx
import React from "react";
import {
  Paper,
  Title,
  Text,
  Group,
  Progress,
  SimpleGrid,
  Badge,
  Box,
} from "@mantine/core";

import { DashboardPlanUsage as DashboardPlanUsageType } from "@/utils/api";

// Helper to format storage in MB/GB
const formatStorageMb = (mb: number): string => {
  if (mb >= 1024) {
    return `${(mb / 1024).toFixed(1)} GB`;
  }
  return `${mb} MB`;
};

// Helper to format large numbers
const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toLocaleString();
};

// Helper to get progress color based on percentage
const getProgressColor = (percent: number): string => {
  if (percent >= 95) return "red";
  if (percent >= 80) return "yellow";
  return "blue";
};

export interface DashboardPlanUsageProps {
  planUsage: DashboardPlanUsageType;
}

export const DashboardPlanUsage: React.FC<DashboardPlanUsageProps> = ({
  planUsage,
}) => {
  return (
    <Paper p="md" radius="md" withBorder>
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <Title order={4} size="h5">
            Plan Usage
          </Title>
          <Badge variant="light" size="sm">
            {planUsage.planName}
          </Badge>
        </Group>
        <Text size="sm" c="dimmed">
          {planUsage.daysRemaining} days remaining in billing period
        </Text>
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
        {/* Submissions usage */}
        <Box>
          <Group justify="space-between" mb={4}>
            <Text size="sm" fw={500}>
              Submissions
            </Text>
            <Text size="sm" c="dimmed">
              {formatNumber(planUsage.submissionsUsed)} /{" "}
              {formatNumber(planUsage.submissionsLimit)}
            </Text>
          </Group>
          <Progress
            value={planUsage.submissionsPercent}
            color={getProgressColor(planUsage.submissionsPercent)}
            size="sm"
            radius="xl"
          />
          {planUsage.willExceedLimit && (
            <Text size="xs" c="orange" mt={4}>
              Projected to exceed limit this period
            </Text>
          )}
        </Box>

        {/* Forms usage */}
        <Box>
          <Group justify="space-between" mb={4}>
            <Text size="sm" fw={500}>
              Forms
            </Text>
            <Text size="sm" c="dimmed">
              {planUsage.formsUsed} / {planUsage.formsLimit}
            </Text>
          </Group>
          <Progress
            value={planUsage.formsPercent}
            color={getProgressColor(planUsage.formsPercent)}
            size="sm"
            radius="xl"
          />
        </Box>

        {/* Storage usage */}
        <Box>
          <Group justify="space-between" mb={4}>
            <Text size="sm" fw={500}>
              Storage
            </Text>
            <Text size="sm" c="dimmed">
              {formatStorageMb(planUsage.storageUsedMb)} /{" "}
              {formatStorageMb(planUsage.storageLimitMb)}
            </Text>
          </Group>
          <Progress
            value={planUsage.storagePercent}
            color={getProgressColor(planUsage.storagePercent)}
            size="sm"
            radius="xl"
          />
        </Box>
      </SimpleGrid>
    </Paper>
  );
};
